import { logger } from '../../../utils/logger';

interface IntercomUserData {
  email?: string;
  user_id?: string;
  name?: string;
  created_at?: number;
  [key: string]: unknown;
}

interface IntercomEventMetadata {
  [key: string]: string | number | boolean | null | IntercomEventMetadata | IntercomEventMetadata[];
}

type IntercomCallback = () => void;
type UnreadCountCallback = (unreadCount: number) => void;
type EmailSuppliedCallback = (email: string) => void;

type IntercomCommandArgs =
  | [command: 'boot', options: IntercomUserData & { app_id: string }]
  | [command: 'shutdown']
  | [command: 'update', options?: IntercomUserData]
  | [command: 'show']
  | [command: 'hide']
  | [command: 'showNewMessage', content?: string]
  | [command: 'trackEvent', eventName: string, metadata?: IntercomEventMetadata]
  | [command: 'showArticle', articleId: number]
  | [command: 'startChecklist', checklistId: number]
  | [command: 'startTour', tourId: number]
  | [command: 'showTicket', ticketId: number]
  | [command: 'onShow', callback: IntercomCallback]
  | [command: 'onHide', callback: IntercomCallback]
  | [command: 'onUnreadCountChange', callback: UnreadCountCallback]
  | [command: 'onUserEmailSupplied', callback: EmailSuppliedCallback];

type IntercomType = (...args: IntercomCommandArgs) => void;

declare global {
  interface Window {
    INTERCOM_APP_ID: string;
    Intercom?: IntercomType;
  }
}

export class Intercom {
  private appId!: string;

  // Assume Intercom is initialized because it's being booted from AngularJS
  // TODO: set to false once React is setting up the user
  private isInitialized: boolean = true;

  private static _instance: Intercom | null = null;

  static get instance(): Intercom {
    // Singleton pattern to ensure only one instance of Intercom is created
    if (!this._instance) {
      throw new Error('Intercom instance not initialized. Call Intercom.init() first.');
    }
    return this._instance;
  }

  constructor(appId: string) {
    if (Intercom._instance) {
      return Intercom._instance;
    }

    this.appId = appId;
    Intercom._instance = this;
  }

  private executeCommand(...args: IntercomCommandArgs): void {
    logger.debug('Intercom', ...args);

    const command = args[0];

    if (command !== 'boot' && command !== 'shutdown' && !this.isInitialized) {
      logger.warn(`Intercom not initialized. Call boot() before ${command}.`);
      return;
    }

    window.Intercom!(...args);
  }

  boot(userData: IntercomUserData = {}): void {
    this.executeCommand('boot', { app_id: this.appId, ...userData });
    this.isInitialized = true;
  }

  shutdown(): void {
    this.executeCommand('shutdown');
    this.isInitialized = false;
  }

  update(userData: IntercomUserData = {}): void {
    this.executeCommand('update', userData);
  }

  show(): void {
    this.executeCommand('show');
  }

  hide(): void {
    this.executeCommand('hide');
  }

  showNewMessage(content?: string): void {
    this.executeCommand('showNewMessage', content);
  }

  trackEvent(eventName: string, metadata: IntercomEventMetadata = {}): void {
    this.executeCommand('trackEvent', eventName, metadata);
  }

  showArticle(articleId: number): void {
    this.executeCommand('showArticle', articleId);
  }

  startChecklist(checklistId: number): void {
    this.executeCommand('startChecklist', checklistId);
  }

  startTour(tourId: number): void {
    this.executeCommand('startTour', tourId);
  }

  showTicket(ticketId: number): void {
    this.executeCommand('showTicket', ticketId);
  }

  onShow(callback: IntercomCallback): void {
    this.executeCommand('onShow', callback);
  }

  onHide(callback: IntercomCallback): void {
    this.executeCommand('onHide', callback);
  }

  onUnreadCountChange(callback: UnreadCountCallback): void {
    this.executeCommand('onUnreadCountChange', callback);
  }

  onUserEmailSupplied(callback: EmailSuppliedCallback): void {
    this.executeCommand('onUserEmailSupplied', callback);
  }
}
